import React, { useState } from 'react';
import {
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Box,
  Typography,
  Checkbox,
  Tooltip,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronRight from '@mui/icons-material/ChevronRight';
import LinkIcon from '@mui/icons-material/Link';
import CloseIcon from '@mui/icons-material/Close';
import { Button } from './ui/Button';

interface ExpansionNode {
  path: string;
  relationName: string;
  level: number;
  isExpanded: boolean;
  children: ExpansionNode[];
}

interface ExpansionTreeViewProps {
  expandableFields: Map<string, { relationName: string; path: string; level: number; parentPath?: string }>;
  expandedPaths: string[];
  onToggle: (path: string) => void;
}

export const ExpansionTreeView: React.FC<ExpansionTreeViewProps> = React.memo(({
  expandableFields,
  expandedPaths,
  onToggle,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  
  // Auto-expand tree nodes for paths that have expanded children
  const getAutoExpandedNodes = (): Set<string> => {
    const autoExpanded = new Set<string>();
    expandedPaths.forEach(path => {
      // For each expanded path, expand all its parent paths in the tree
      const parts = path.split('.');
      for (let i = 0; i < parts.length - 1; i++) {
        const parentPath = parts.slice(0, i + 1).join('.');
        autoExpanded.add(parentPath);
      }
    });
    return autoExpanded;
  };
  
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(() => getAutoExpandedNodes());
  
  // Update expanded nodes when expandedPaths change
  const expandedPathsKey = expandedPaths.join(',');
  React.useEffect(() => {
    setExpandedNodes(getAutoExpandedNodes());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedPathsKey]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (_?: any, reason?: string) => {
    if (reason === 'escapeKeyDown' || reason === 'backdropClick') {
      setAnchorEl(null);
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'expansion-tree-popover' : undefined;

  // Build tree structure from flat expandable fields map
  const buildTree = (): ExpansionNode[] => {
    const tree: ExpansionNode[] = [];
    const nodeMap = new Map<string, ExpansionNode>();

    // Sort by path depth (level 0 first)
    const sortedFields = Array.from(expandableFields.entries()).sort((a, b) => {
      return a[1].level - b[1].level;
    });

    sortedFields.forEach(([_, info]) => {
      const node: ExpansionNode = {
        path: info.path,
        relationName: info.relationName,
        level: info.level,
        isExpanded: expandedPaths.includes(info.path),
        children: [],
      };

      nodeMap.set(info.path, node);

      if (info.parentPath) {
        // This is a nested field, add to parent
        const parent = nodeMap.get(info.parentPath);
        if (parent) {
          parent.children.push(node);
        } else {
          // Parent not yet in map, add to root for now
          tree.push(node);
        }
      } else {
        // This is a root level field
        tree.push(node);
      }
    });

    return tree;
  };

  const handleNodeToggle = (path: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const capitalize = (text: string) => {
    return text
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())
      .trim();
  };

  const renderTreeNode = (node: ExpansionNode): React.ReactNode => {
    const hasChildren = node.children.length > 0;
    const isNodeExpanded = expandedNodes.has(node.path);
    const isDataExpanded = node.isExpanded;

    return (
      <React.Fragment key={node.path}>
        <ListItem
          dense
          sx={{
            pl: node.level * 2 + 1,
            py: 0.75,
            transition: 'all 0.15s ease-in-out',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: isDataExpanded 
                ? 'color-mix(in srgb, var(--od-primary) 6%, transparent)'
                : 'color-mix(in srgb, var(--od-muted) 4%, transparent)',
            },
            borderLeft: node.level > 0 
              ? `3px solid ${isDataExpanded ? 'color-mix(in srgb, var(--od-primary) 35%, transparent)' : 'color-mix(in srgb, var(--od-primary) 20%, transparent)'}` 
              : 'none',
            marginLeft: node.level > 0 ? `${node.level * 12}px` : 0,
            backgroundColor: isDataExpanded 
              ? 'color-mix(in srgb, var(--od-primary) 4%, transparent)'
              : 'transparent',
            borderRadius: '6px',
            mb: 0.5,
          }}
          onClick={() => onToggle(node.path)}
        >
          {hasChildren ? (
            <IconButton
              size="small"
              aria-label={capitalize(node.relationName)}
              aria-expanded={isNodeExpanded}
              onClick={(e) => { e.stopPropagation(); handleNodeToggle(node.path); }}
              sx={{ 
                mr: 0.5, 
                padding: '4px',
                transition: 'transform 0.2s ease',
                '&:hover': { backgroundColor: 'color-mix(in srgb, var(--od-muted) 8%, transparent)' },
              }}
            >
              {isNodeExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
            </IconButton>
          ) : (
            <Box sx={{ width: 28, mr: 0.5 }} />
          )}
          <ListItemIcon sx={{ minWidth: 32 }}>
            <LinkIcon 
              fontSize="small" 
              sx={{ 
                color: isDataExpanded ? 'var(--od-primary)' : 'var(--od-muted)',
                opacity: isDataExpanded ? 1 : 0.5,
                transition: 'all 0.2s ease',
              }} 
            />
          </ListItemIcon>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <span>{capitalize(node.relationName)}</span>
                {hasChildren && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: isNodeExpanded ? 'var(--od-primary-text)' : 'var(--od-muted)',
                      fontSize: '0.75rem',
                      backgroundColor: isNodeExpanded 
                        ? 'color-mix(in srgb, var(--od-primary) 12%, transparent)'
                        : 'color-mix(in srgb, var(--od-muted) 6%, transparent)',
                      padding: '2px 6px',
                      borderRadius: '10px',
                      fontWeight: isNodeExpanded ? 600 : 500,
                      lineHeight: 1.3,
                      border: isNodeExpanded 
                        ? '1px solid color-mix(in srgb, var(--od-primary) 35%, transparent)'
                        : '1px solid transparent',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {node.children.length}
                  </Typography>
                )}
              </Box>
            }
            secondary={node.path}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: isDataExpanded ? 600 : 400,
              color: isDataExpanded ? 'var(--od-text)' : 'var(--od-muted)',
            }}
            secondaryTypographyProps={{
              fontSize: '0.75rem',
              color: 'var(--od-muted)',
            }}
          />
          <Tooltip title={isDataExpanded ? 'Click to collapse' : 'Click to expand'}>
            <Checkbox
              size="small"
              inputProps={{ 'aria-label': capitalize(node.relationName) }}
              checked={isDataExpanded}
              onChange={() => onToggle(node.path)}
              onClick={(e) => e.stopPropagation()}
              sx={{ 
                padding: '6px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: isDataExpanded 
                    ? 'color-mix(in srgb, var(--od-primary) 8%, transparent)'
                    : undefined,
                },
              }}
            />
          </Tooltip>
        </ListItem>
        {hasChildren && (
          <Collapse in={isNodeExpanded} timeout={200} unmountOnExit>
            <List component="div" disablePadding sx={{ pl: 0.5 }}>
              {node.children.map((child) => renderTreeNode(child))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const tree = buildTree();

  if (tree.length === 0) {
    return null; // Don't show button if no expandable fields
  }

  const totalRelations = tree.length;
  const expandedCount = expandedPaths.length;

  return (
    <>
      <Tooltip title="Browse related records">
        <Button
          onClick={handleClick}
          aria-label="Browse related records"
          variant="secondary"
          size="sm"
        >
          <AccountTreeIcon sx={{ fontSize: 15 }} />
          <span className="whitespace-nowrap">
            Relations
            <span className="ml-1 text-od-muted">
              {expandedCount}/{totalRelations}
            </span>
          </span>
        </Button>
      </Tooltip>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        keepMounted
        disableRestoreFocus
        disableEnforceFocus
        disableAutoFocus
        disableScrollLock={false}
        marginThreshold={8}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        BackdropProps={{
          invisible: true,
          sx: { pointerEvents: 'auto', cursor: 'default' }
        }}
        PaperProps={{
          elevation: 0,
          className: 'border border-od-border bg-od-surface',
          sx: {
            mt: 0.5,
            maxHeight: '70vh',
            width: 'min(520px, calc(100vw - 16px))',
            maxWidth: 'calc(100vw - 16px)',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow:
              '0 8px 32px color-mix(in srgb, var(--od-muted) 12%, transparent), 0 2px 8px color-mix(in srgb, var(--od-muted) 8%, transparent)',
          },
        }}
      >
        <div className="flex items-center justify-between gap-2 border-b border-od-border bg-od-surface px-3 py-2">
          <div className="min-w-0">
            <h2 className="m-0 text-sm font-semibold text-od-text">Relations</h2>
            <p className="m-0 text-xs text-od-muted">
              {expandedCount} of {totalRelations} expanded
            </p>
          </div>
          <Button
            aria-label="Close relations"
            onClick={() => setAnchorEl(null)}
            variant="ghost" size="icon"
          >
            <CloseIcon fontSize="small" />
          </Button>
        </div>
        <List
          sx={{
            width: '100%',
            bgcolor: 'var(--od-surface)',
            maxHeight: 'calc(70vh - 56px)',
            overflow: 'auto',
            py: 1,
            px: 1,
          }}
        >
          {tree.map((node) => (
            <React.Fragment key={node.path}>
              {renderTreeNode(node)}
            </React.Fragment>
          ))}
        </List>
      </Popover>
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: skip render if expandable fields and paths are identical
  // DO NOT compare callback to prevent popover state reset
  return (
    prevProps.expandableFields === nextProps.expandableFields &&
    prevProps.expandedPaths === nextProps.expandedPaths
  );
});

